import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Form, Formik} from 'formik';
import {Trans} from 'react-i18next';
import {history} from '../../../../utils/redux/store';
import skinDetectionCategories from '../../../../utils/config/skinDetectionCategories';
import _get from 'lodash/get';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import Header from '../../components/header';
import ButtonWithRestoreGroup from '../../../../components/formik/ButtonWithRestoreGroup';
import {
  handleImage,
  getTemplateDefaultConfig,
  mappingDisable,
  getTemplate,
  getTemplateLayout,
  updateTemplate,
  getBrandId,
  getDisabledDetectionCategoriesByPlan,
  handleGenericTemplateExport,
  getConcernOrderWithScore,
} from '../../../../utils/skincareUtils';
import Loader from '../../../../components/spinner/Loader';
import smbUtils from '../../../../utils/smbUtils';
import PreviewModule from '../../components/previewmodule';
import PublishToOnlineModal from '../components/PublishToOnlineModal';
import {
  deviceOptions,
  getAvailableDevices,
  getUiConfigDevice,
  setUiConfigDevice,
} from '../config/utils/optionsUtils';
import config from '../config/utils/contentUtils';
import useSkincare from '../../../../components/hook/useSkincare';
import langUtils from '../../../../utils/langUtils';
import {useQuery} from '../../../../components/hook/useQuery';
import langCode from '../../../../utils/langUtils';
import skincareProDao from '../../../../dao/skincareProDao';
import {privilegeUtils} from '../../../../utils/privilegeUtils';
import GenericLayoutSettings from './components/GenericLayoutSettings';
import ChooseUITemplate from '../components/ChooseUITemplate';
import CustomizedLayoutSettings from './components/CustomizedLayoutSettings';
import AccessOptionsHintModal from '../components/AccessOptionsHintModal';
import useSolutionType from '../../../../components/hook/useSolutionType';
import {isFunctionBusyError} from '../../../../utils/errorUtils';
import {detectionMap, detectionHdMap} from '../skincaredetection/preview/setting';
import ElevatedButton from '../../../../components/formik/ElevatedButton';
import ConfigurationModal from '../components/ConfigurationModal';
import OverLayLoader from '../../../../components/spinner/OverLayLoader';

export default function SkinResultPageV2() {
  const accountSummary = useSelector(
    state => state.components.auth.accountSummary
  );
  const {token, customerId, brandInfo} = smbUtils.customerInfo(accountSummary);
  const formRef = useRef();
  const isTechnicalSupport = privilegeUtils.isTechnicalSupport(accountSummary);

  const [isLoaded, setIsLoaded] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [initValue, setInitValue] = useState({});
  const [commonData, setCommonData] = useState({});
  const [templateId, setTemplateId] = useState('');
  const [customTempDataset, setCustomTempDataset] = useState([]);
  const [generalTempInfo, setGeneralTempInfo] = useState({});
  const [selectedTempInfo, setSelectedTempInfo] = useState({});
  const [device, setDevice] = useState(
    getUiConfigDevice(deviceOptions, customerId)
  );
  const [language, setLanguage] = useState(langUtils.getInitialLang());

  const [publishStatus, setPublishStatus] = useState(false);
  const [isShowPublishModal, setIsShowPublishModal] = useState(false);
  const [isShowAccessOptionsModal, setIsShowAccessOptionsModal] = useState(
    false
  );
  const [modalState, setModalState] = useState({
    isShow: false,
    type: null,
    showFooter: false,
  });
  const [customSkinConcernData, setCustomSkinConcernData] = useState({});
  const templateType = 'ResultHomePage';
  const templateType2 = 'SmbSkincareResultPage';
  const templateType3 = 'SmbResultHomePage';
  const [tab, setTab] = useState(templateType2);

  const [category, setCategory] = useState([]);
  const [categoryDisplayOrder, setCategoryDisplayOrder] = useState('manual');
  let displayRecommendLayout = [];
  const {isV1, isVersionAbove2AndHd, isSupportSubOptions} = useSkincare(
    accountSummary
  );

  const concernOrderWithScore = useMemo(() => {
    const scoreMap = isVersionAbove2AndHd ? detectionHdMap : detectionMap;
    return getConcernOrderWithScore(category, categoryDisplayOrder, scoreMap);
  }, [category, categoryDisplayOrder, isVersionAbove2AndHd]);

  const {isSkinAnalysisPremium} = useSolutionType(accountSummary);
  const subscriptionMode = isSkinAnalysisPremium ? 'Premium' : 'Essential';
  const transformedCategories = skinDetectionCategories
    .getSkinDetectionReportFlatConcern(
      false,
      isVersionAbove2AndHd,
      isSupportSubOptions
    )
    .filter(
      item =>
        !getDisabledDetectionCategoriesByPlan(accountSummary).includes(
          item.value
        )
    );
  const defaultCardCategory = isVersionAbove2AndHd ? 'hd_spot' : 'spot';

  const {
    data: templateUrlData,
    isLoading: isLoadingByTemplateUrl,
    refetch: tempPreviewUrlRefetch,
  } = useQuery({
    queryFunc: params => {
      if (!_get(params, 'rvId')) {
        return;
      }
      return _get(params, 'isDefault')
        ? getTemplateLayout(
            token,
            customerId,
            templateType2,
            _get(brandInfo, 'templateVersion')
          )
        : getTemplate(token, customerId, _get(params, 'rvId'));
    },
    initData: {
      indexPage: '',
      getPhotoPage: '',
      indexFileUrl: '',
      getPhotoFileUrl: '',
    },
    isInitEnabled: false,
  });

  useEffect(() => {
    if (isV1) {
      history.push('/skincare-pro/ui-configuration/skin-result-page');
    }
    fetchData();
  }, []);

  const updateCustomLang = availableLangs => {
    const availableLangList = langCode.getConsoleStringLangList();
    const matchedLang = availableLangList.find(lang =>
      availableLangs.includes(lang.value)
    );
    setLanguage(matchedLang || availableLangList[0]);
  };

  const updateCustomDevice = availableDevices => {
    setDevice(
      getUiConfigDevice(getAvailableDevices(availableDevices), customerId)
    );
  };

  const fetchData = async () => {
    setIsLoaded(true);
    try {
      const promises = [
        skincareProDao.listUIConfigTemplates({template_type: templateType}),
      ];

      if (isVersionAbove2AndHd) {
        promises.push(
          skincareProDao.getCustomerSetting({
            type: 'ScpAppSettings',
          })
        );
      }

      const results = await Promise.all(
        promises.map(promise => promise.catch(() => ({})))
      );

      const templateLists = results[0];
      const appSettingsConfig = isVersionAbove2AndHd ? results[1] : null;

      if (isVersionAbove2AndHd && appSettingsConfig) {
        const customSkinConcern = _get(
          appSettingsConfig,
          'editing_revision.data.custom_skin_concern_name',
          {}
        );
        setCustomSkinConcernData(customSkinConcern);
      }

      const templates = _get(templateLists, 'templates', []);
      setCustomTempDataset(templates.filter(template => !template.is_default));

      const defaultItem = templates.find(t => t.is_default);
      if (!defaultItem) {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
        return;
      }
      setGeneralTempInfo(defaultItem);
      //For general template form
      await getTemplate(
        token,
        customerId,
        _get(defaultItem, 'template_revision_id')
      ).then(res => {
        if (res && res.configJson) {
          const parsedConfig = JSON.parse(res.configJson);

          // check if analysisGetPhoto page exists
          const pages = _get(parsedConfig, 'page', []);
          const hasAnalysisGetPhoto = pages.some(
            p => p.pageType === 'analysisGetPhoto'
          );

          let finalPages = [];
          if (!hasAnalysisGetPhoto && pages.length > 0) {
            const existingPage =
              pages.find(p => p.pageType === 'getSkincareResult') || pages[0];

            finalPages = [
              existingPage,
              {
                pageType: 'analysisGetPhoto',
                templateType: _get(existingPage, 'templateType', 'skincare'),
                is_default: _get(existingPage, 'is_default', true),
                template_id: _get(existingPage, 'template_id', ''),
                template_revision_id: _get(
                  existingPage,
                  'template_revision_id',
                  null
                ),
                accessOptions: {
                  showQRCode: true,
                  showEmail: true,
                  showPrint: true,
                },
              },
            ];
          } else {
            finalPages = pages;
          }

          setInitValue({
            page: finalPages,
            is_default: _get(res, 'is_default', true),
            template_id: _get(res, 'templateId', ''),
            template_revision_id: _get(
              defaultItem,
              'template_revision_id',
              null
            ),
          });
        }
        setTemplateId(_get(res, 'templateId', ''));
      });

      //For general template post massage
      await getBrandId(token, customerId).then(async res => {
        if (res && res.commonSettingRvId) {
          await getTemplate(
            token,
            customerId,
            _get(res, 'commonSettingRvId'),
            'CommonSetting'
          ).then(res => {
            if (res && res.configJson) {
              setCommonData(JSON.parse(res.configJson));
            }
          });
        }
        if (res && res.skincareDetectionRvId) {
          await getTemplate(
            token,
            customerId,
            _get(res, 'skincareDetectionRvId')
          ).then(res => {
            const processCategory = array =>
              array.map(r => {
                switch (r) {
                  case 'age_spot':
                    return 'spot';
                  case 'dark_circle_v2':
                    return 'darkCircle';
                  default:
                    return r;
                }
              });
            if (res) {
              const detectionData = JSON.parse(_get(res, 'metaDataJson', ''));
              setCategoryDisplayOrder(_get(detectionData, 'categoryDisplayOrder', 'manual'));
              setCategory(
                _get(detectionData, 'isShowSkinHealth', false)
                  ? [
                      'skin_health',
                      ...processCategory(
                        _get(detectionData, 'sequenceOfCategory', [])
                      ),
                    ]
                  : processCategory(
                      _get(detectionData, 'sequenceOfCategory', [])
                    )
              );
            }
          });
        }
      });

      const selectedItem = templates.find(t => t.selected);
      if (!selectedItem) {
        toast.error(
          <Trans i18nKey="toast.error.loading.data.failed">
            Failed to load data.
          </Trans>
        );
        return;
      }

      if (selectedItem.is_default) {
        setSelectedTempInfo({
          ...selectedItem,
          device_type: deviceOptions.map(opt => opt.value),
          langs: langCode.getConsoleStringLangList().map(opt => opt.value),
        });
      } else {
        setSelectedTempInfo(selectedItem);
        updateCustomLang(_get(selectedItem, 'langs', []));
        updateCustomDevice(_get(selectedItem, 'device_type', []));
      }

      await tempPreviewUrlRefetch({
        isDefault: selectedItem.is_default,
        rvId: selectedItem.template_revision_id,
      });
    } catch (error) {
      toast.error(
        <Trans i18nKey="toast.error.loading.data.failed">
          Failed to load data.
        </Trans>
      );
    } finally {
      setIsLoaded(false);
    }
  };

  const handleRestore = () => {
    getTemplateDefaultConfig(
      templateType3,
      subscriptionMode,
      2,
      customerId
    ).then(r => {
      if (r && r.pageConfig && r.templateMetadata) {
        updateTemplate(
          token,
          customerId,
          templateId,
          templateType,
          r.templateMetadata,
          r.fileId,
          r.pageConfig
        ).then(() => {
          setPublishStatus(!publishStatus);
          toast.success(
            <Trans i18nKey="c.f.simple.button.group.success">success</Trans>
          );
          window.location.reload();
        });
      }
    });
  };

  const getInitValue = values => {
    const v = values || {};
    const getSkincareResultPage = _get(v, 'page[0]', {});
    const analysisGetPhotoPage = _get(v, 'page[1]', {});
    const layout = _get(
      getSkincareResultPage,
      'recommendation.tabs',
      []
    ).filter(r => r.enable);
    const array =
      layout.length > 0
        ? layout.map(({value}) => value)
        : ['productRecommend', 'treatmentRecommend', 'routineRecommend'];
    const sequenceOfRecommendLayout = mappingDisable(
      array,
      skinDetectionCategories.getSkinRecommendLayout()
    );
    displayRecommendLayout =
      sequenceOfRecommendLayout.length > 0 ? sequenceOfRecommendLayout : [];

    const {page, ...restV} = v;

    return {
      ...restV,
      skinResultPage: {
        ...getSkincareResultPage,
        general: {
          ...getSkincareResultPage.general,
          getSkinReportButtonText: _get(
            getSkincareResultPage,
            'general.getSkinReportButtonText.enu'
          )
            ? _get(getSkincareResultPage, 'general.getSkinReportButtonText')
            : config.getSkinReportButtonText,
        },
        skinReport: {
          ...getSkincareResultPage.skinReport,
          resultPage: {
            ..._get(getSkincareResultPage, 'skinReport.resultPage', {}),
            showSkinType: _get(
              getSkincareResultPage,
              'skinReport.resultPage.showSkinType',
              false
            ),
          },
        },
        recommendation: getSkincareResultPage.recommendation,
      },
      getPhotoPage: {
        ...analysisGetPhotoPage,
        accessOptions: {
          showQRCode: _get(
            analysisGetPhotoPage,
            'accessOptions.showQRCode',
            true
          ),
          showEmail: _get(
            analysisGetPhotoPage,
            'accessOptions.showEmail',
            true
          ),
          showPrint: _get(
            analysisGetPhotoPage,
            'accessOptions.showPrint',
            true
          ),
        },
      },
    };
  };

  const handleSelectedTemplate = async (formikProps, targetItem) => {
    formikProps.setFieldValue(
      'is_default',
      _get(targetItem, 'is_default', true)
    );
    formikProps.setFieldValue(
      'template_id',
      _get(targetItem, 'template_id', null)
    );
    formikProps.setFieldValue(
      'template_revision_id',
      _get(targetItem, 'template_revision_id', null)
    );
    setSelectedTempInfo(targetItem);
    updateCustomLang(_get(targetItem, 'langs', []));
    updateCustomDevice(_get(targetItem, 'device_type', []));
    await tempPreviewUrlRefetch({
      isDefault: targetItem.is_default,
      rvId: targetItem.template_revision_id,
    });
  };

  const handleExport = async (templateId, isDirty) => {
    await handleGenericTemplateExport({
      templateId,
      fileName: 'SkinResultPage_Generic',
      setIsExporting,
      isDirty,
      setModalState,
    });
  };

  const formikConfig = {
    enableReinitialize: true,
    initialValues: getInitValue(initValue),
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true);

      const {showQRCode, showEmail, showPrint} =
        values.getPhotoPage.accessOptions || {};
      if (!showQRCode && !showEmail && !showPrint) {
        setIsShowAccessOptionsModal(true);
        setSubmitting(false);
        return;
      }

      if (
        !privilegeUtils.canCustomizationAppLayout(accountSummary) &&
        !values.is_default
      ) {
        toast.error(
          <Trans i18nKey="toast.error.customized.version.failed">
            If you need to set up a customized template, please go to Help >
            Contact Us.
          </Trans>
        );
        setSubmitting(false);
        return;
      }

      if (!values.is_default && !values.template_id) {
        toast.error(
          <Trans i18nKey="toast.error.update.template.failed">
            Failed to update, please choose a template.
          </Trans>
        );
        setSubmitting(false);
        return;
      }

      if (!values.is_default) {
        await skincareProDao
          .selectUIConfigTemplate({
            ...selectedTempInfo,
            template_type: templateType,
          })
          .then(r => {
            setIsShowPublishModal(true);
          })
          .catch(r => {
            toast.error(
              <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
            );
            setSubmitting(false);
          });
        return;
      }

      const phoneUrl = await handleImage(
        _get(
          values,
          'skinResultPage.recommendation.backgroundImage[0].imageUrl'
        ),
        token,
        customerId
      );

      const padUrl = await handleImage(
        _get(
          values,
          'skinResultPage.recommendation.backgroundImage[1].imageUrl'
        ),
        token,
        customerId
      );

      const padLandscapeUrl = await handleImage(
        _get(
          values,
          'skinResultPage.recommendation.backgroundImage[2].imageUrl'
        ),
        token,
        customerId
      );

      const data = {
        page: [
          {
            templateType: 'skincare',
            pageType: 'getSkincareResult',
            ...values.skinResultPage,
            recommendation: {
              ...values.skinResultPage.recommendation,
              backgroundImage: [
                {
                  type: 'phone',
                  image:
                    './assets/images/img_skincarepro_resultpg_topbg_phone.jpg',
                  imageUrl: await phoneUrl,
                },
                {
                  type: 'pad',
                  image:
                    './assets/images/img_skincarepro_resultpg_topbg_pad.jpg',
                  imageUrl: await padUrl,
                },
                {
                  type: 'padLandscape',
                  image:
                    './assets/images/img_skincarepro_resultpg_topbg_landscape.jpg',
                  imageUrl: await padLandscapeUrl,
                },
              ],
              tabs: values.skinResultPage.recommendation.tabs.map(r => ({
                enable: !r.disabled,
                value: r.value,
              })),
            },
          },
          {
            templateType: 'skincare',
            pageType: 'analysisGetPhoto',
            ...values.getPhotoPage,
          },
        ],
      };

      await updateTemplate(
        token,
        customerId,
        templateId,
        templateType,
        {
          files: [
            {
              file_url: phoneUrl,
              path: './assets/images/img_skincarepro_resultpg_topbg_phone.jpg',
            },
            {
              file_url: padUrl,
              path: './assets/images/img_skincarepro_resultpg_topbg_pad.jpg',
            },
            {
              file_url: padLandscapeUrl,
              path:
                './assets/images/img_skincarepro_resultpg_topbg_landscape.jpg',
            },
          ],
        },
        '',
        data
      )
        .then(async () => {
          if (privilegeUtils.canCustomizationAppLayout(accountSummary)) {
            try {
              await skincareProDao.selectUIConfigTemplate({
                ...selectedTempInfo,
                template_type: templateType,
              });
            } catch (err) {
              toast.error(
                <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
              );
            }
          }

          setIsShowPublishModal(true);
        })
        .catch(e => {
          if (isFunctionBusyError(e)) {
            setModalState({
              isShow: true,
              type: 'processingInProgress',
              showFooter: false,
            });
            setSubmitting(false);
            return;
          }

          toast.error(
            <Trans i18nKey="c.f.simple.button.group.failed">failed</Trans>
          );
          setSubmitting(false);
        });
      await setSubmitting(false);
    },
  };

  if (
    !(
      privilegeUtils.canUiConfig(accountSummary) ||
      privilegeUtils.isTechnicalSupport(accountSummary)
    )
  ) {
    history.push('/');
  }

  return isLoaded ? (
    <div>
      <Header />
      <Loader />
    </div>
  ) : (
    <>
      <Formik {...formikConfig} ref={formRef}>
        {formikProps => (
          <Form className="skincare-configuration-form">
            <Header publishStatus={publishStatus} dirty={formikProps.dirty} />
            <div className="skincare-configuration-layout">
              <div className="skincare-configuration-layout__left">
                <div className="kt-portlet skincare-configuration-layout__left-portlet">
                  <div className="kt-portlet__body pb-0">
                    <ChooseUITemplate
                      isDefaultLayout={_get(selectedTempInfo, 'is_default')}
                      setIsDefaultLayout={v => {
                        let targetItem = customTempDataset.find(
                          item => item.is_default === false
                        );
                        if (!targetItem) {
                          targetItem = {
                            is_default: v,
                            template_id: null,
                            template_revision_id: null,
                            device_type: deviceOptions.map(op => op.value),
                            langs: langCode
                              .getConsoleStringLangList()
                              .map(op => op.value),
                          };
                        }
                        if (v) {
                          targetItem = {
                            ...generalTempInfo,
                            is_default: true,
                            device_type: deviceOptions.map(op => op.value),
                            langs: langCode
                              .getConsoleStringLangList()
                              .map(op => op.value),
                          };
                        }
                        handleSelectedTemplate(formikProps, targetItem);
                      }}
                    />
                  </div>
                  <div className="kt-portlet__body skincare-configuration-layout__left-portlet__body">
                    {_get(selectedTempInfo, 'is_default') && (
                      <GenericLayoutSettings
                        formikProps={formikProps}
                        device={device}
                        setDevice={setDevice}
                        defaultCardCategory={defaultCardCategory}
                        transformedCategories={transformedCategories}
                        displayRecommendLayout={displayRecommendLayout}
                        tab={tab}
                        setTab={setTab}
                      />
                    )}
                    {!_get(selectedTempInfo, 'is_default') && (
                      <CustomizedLayoutSettings
                        data={customTempDataset}
                        isLoading={isLoaded}
                        selectedTempInfo={selectedTempInfo}
                        handleSelectedTemplate={targetItem =>
                          handleSelectedTemplate(formikProps, targetItem)
                        }
                        tab={tab}
                        setTab={setTab}
                        isTechnicalSupport={isTechnicalSupport}
                      />
                    )}
                  </div>
                  <div className="d-flex justify-content-between kt-margin-t-20">
                    {isTechnicalSupport &&
                    _get(selectedTempInfo, 'is_default') ? (
                      <div className="align-self-center kt-margin-l-10">
                        <ElevatedButton
                          type="custom"
                          faIcon="fa-download"
                          text={
                            <Trans i18nKey="p.s.uiconfiguration.export.current.ui.html.file">
                              Export Current UI As HTML File
                            </Trans>
                          }
                          onClick={() =>
                            handleExport(
                              selectedTempInfo.template_id,
                              formikProps.dirty
                            )
                          }
                          disabled={isExporting}
                        />
                      </div>
                    ) : (
                      <div />
                    )}
                    <div>
                      <ButtonWithRestoreGroup
                        saveDirty={formikProps.dirty}
                        discardButton={false}
                        isUpdating={true}
                        onRestoreClick={() => {
                          handleRestore();
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {!isLoadingByTemplateUrl && formikProps.values && (
                <div className="skincare-configuration-layout__right">
                  <PreviewModule
                    values={formikProps.values}
                    template={{
                      ...(_get(selectedTempInfo, 'is_default') && {
                        page: {
                          page: [
                            {
                              ...formikProps.values.skinResultPage,
                              preview: {
                                concernOrder: category,
                                ...(concernOrderWithScore && {
                                  concernOrderWithScore: concernOrderWithScore,
                                }),
                                ...(isVersionAbove2AndHd && {
                                  customName: {
                                    custom_skin_concern_name: customSkinConcernData,
                                  },
                                }),
                              },
                              recommendation: {
                                ...formikProps.values.skinResultPage
                                  .recommendation,
                                backgroundImage: [
                                  {
                                    type: 'phone',
                                    image: _get(
                                      formikProps,
                                      'values.skinResultPage.recommendation.backgroundImage[0].imageUrl'
                                    ),
                                  },
                                  {
                                    type: 'pad',
                                    image: _get(
                                      formikProps,
                                      'values.skinResultPage.recommendation.backgroundImage[1].imageUrl'
                                    ),
                                  },
                                  {
                                    type: 'padLandscape',
                                    image: _get(
                                      formikProps,
                                      'values.skinResultPage.recommendation.backgroundImage[2].imageUrl'
                                    ),
                                  },
                                ],
                                tabs: _get(
                                  formikProps,
                                  'values.skinResultPage.recommendation.tabs',
                                  []
                                ).map(r => ({
                                  enable: !r.disabled,
                                  value: r.value,
                                })),
                              },
                            },
                            {
                              ...formikProps.values.getPhotoPage,
                              accessOptions: {
                                ...formikProps.values.getPhotoPage.accessOptions,
                              },
                            },
                          ],
                        },
                      }),
                      common: {
                        ...commonData,
                        page: [
                          {
                            ..._get(commonData, 'page[0]'),
                            header: {
                              logo: {
                                image: _get(
                                  commonData,
                                  'page[0].header.logo.imageUrl',
                                  ''
                                ),
                              },
                              displayName: _get(
                                commonData,
                                'page[0].header.displayName',
                                'showAll'
                              ),
                            },
                            isHDAnalysis: _get(
                              commonData,
                              'page[0].isHDAnalysis',
                              isVersionAbove2AndHd
                            ),
                            currencySymbol: {
                              enableSkincareProduct: _get(
                                commonData,
                                'page[0].currencySymbol.enableSkincareProduct',
                                true
                              ),
                              enableSkincareTreatment: _get(
                                commonData,
                                'page[0].currencySymbol.enableSkincareTreatment',
                                true
                              ),
                            },
                          },
                        ],
                      },
                    }}
                    previewUrl={
                      selectedTempInfo.is_default
                        ? tab === templateType2
                          ? _get(templateUrlData, 'indexPage')
                          : _get(templateUrlData, 'getPhotoPage')
                        : tab === templateType2
                        ? _get(templateUrlData, 'indexFileUrl')
                        : _get(templateUrlData, 'getPhotoFileUrl')
                    }
                    lang={language}
                    type={'ResultPageV2'}
                    deviceList={getAvailableDevices(
                      deviceOptions,
                      _get(selectedTempInfo, 'device_type', [])
                    )}
                    langList={langUtils.getAvailableConsoleStringLangList(
                      _get(selectedTempInfo, 'langs', [])
                    )}
                    onChangeDevice={device =>
                      setDevice(setUiConfigDevice(device))
                    }
                    onChangeLanguage={lang => {
                      formikProps.setFieldValue(
                        'recommendation.text.textLanguage',
                        lang
                      );
                      formikProps.setFieldValue(
                        'skinReport.summary.description.textLanguage',
                        lang
                      );
                      formikProps.setFieldValue(
                        'general.getSkinReportButtonText.textLanguage',
                        lang
                      );
                    }}
                    isCustomized={!_get(selectedTempInfo, 'is_default', false)}
                  />
                </div>
              )}
            </div>
            <PublishToOnlineModal
              isShow={isShowPublishModal}
              onPublish={isPublish => {
                formikProps.setSubmitting(false);
                formikProps.resetForm(formikProps.values);
                setPublishStatus(isPublish);
                setIsShowPublishModal(false);
              }}
            />
            <AccessOptionsHintModal
              show={isShowAccessOptionsModal}
              onHide={() => setIsShowAccessOptionsModal(false)}
            />
            <ConfigurationModal
              modalState={modalState}
              onClose={() => setModalState({isShow: false, type: null})}
            />
          </Form>
        )}
      </Formik>
      {isExporting && (
        <OverLayLoader
          text={
            <Trans i18nKey="c.spinner.overlay.loader.exporting">
              Exporting...
            </Trans>
          }
        />
      )}
    </>
  );
}
